// Pagina HTML a raportului "Consum materiale pe perioada" (tabul "Rapoarte perioada"
// din Produse finite). Primeste raspunsul de la /api/rapoarte/consum-perioada si
// intoarce un document HTML complet, A4 landscape, cu o bara de sus (butonul de
// Print + Inchide) care NU apare la tiparire. Se deschide intr-o fereastra noua;
// tiparirea se face abia cand utilizatorul apasa Print pe acea pagina.

import { formatCantitate } from './format';
import { formatNumarRaport } from './export';

export const FORMA_FACTURA = 'factura';
export const FORMA_PRODUCTIE = 'productie';

function esc(text) {
  return String(text ?? '')
    .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
}

// "2026-02-28" -> "28.02.2026" (fara obiect Date, ca sa nu apara decalaje de fus orar)
function dataRo(iso) {
  if (!iso) return '';
  const [an, luna, zi] = String(iso).slice(0, 10).split('-');
  return `${zi}.${luna}.${an}`;
}

const lei = v => formatNumarRaport(v);
// Pretul unitar: minim 2, maxim 4 zecimale - media ponderata a facturilor (ex. 2,2222) trebuie sa se vada
// ca sa se poata reface valoarea = cantitate x pret.
const pret = v => Number(v).toLocaleString('ro-RO', { minimumFractionDigits: 2, maximumFractionDigits: 4 });
const cant = v => formatCantitate(v);
const zeroLiniuta = (v, formateaza) => (Number(v) === 0 ? '-' : formateaza(v));
const denumireMaterial = m => `${m.cod ? m.cod + ' - ' : ''}${m.denumire}`;

const STIL = `
  @page { size: A4 landscape; margin: 10mm; }
  * { box-sizing: border-box; }
  body { font-family: Arial, Helvetica, sans-serif; font-size: 10.5px; color: #111; margin: 0; background: #eef0f4; }
  .bara { position: sticky; top: 0; z-index: 5; background: #16213e; color: #fff; padding: 10px 18px;
          display: flex; align-items: center; gap: 12px; font-size: 13px; }
  .bara .spatiu { flex: 1; }
  .bara button { border: none; border-radius: 6px; padding: 8px 18px; font-size: 14px; font-weight: 600; cursor: pointer; }
  .bara .print { background: #27ae60; color: #fff; }
  .bara .inchide { background: #f1f3f8; color: #3a4257; }
  .pagina { background: #fff; width: 297mm; margin: 14px auto; padding: 10mm; box-shadow: 0 2px 10px rgba(0,0,0,.15); }
  h1 { font-size: 17px; margin: 0 0 3px; }
  .meta { color: #444; margin: 0 0 10px; font-size: 11px; }
  h3 { font-size: 12px; margin: 14px 0 4px; page-break-after: avoid; }
  table { width: 100%; border-collapse: collapse; margin-bottom: 6px; }
  th, td { border: 1px solid #8a8f9a; padding: 3px 5px; vertical-align: top; }
  th { background: #e4e8f1; text-align: center; font-weight: 700; }
  thead { display: table-header-group; }
  tr { page-break-inside: avoid; }
  td.n { text-align: right; white-space: nowrap; }
  td.nr { text-align: center; width: 6%; }
  tr.grup td { background: #f3f5fa; font-weight: 700; font-size: 11px; }
  tr.grup .um { font-weight: 400; color: #555; }
  tr.sub td { background: #f7f8fb; font-weight: 700; }
  tr.tot td { background: #dfe5f2; font-weight: 700; font-size: 11.5px; }
  tr.fara td { color: #9a3412; font-style: italic; }
  .bloc { page-break-inside: avoid; break-inside: avoid; }
  tbody.mat { page-break-inside: avoid; break-inside: avoid; }
  table.t2 th:nth-child(2) { width: 46%; }
  table.t2 th:nth-child(3) { width: 8%; }
  table.general { width: 60%; margin-top: 4px; }
  .nota { font-size: 9.5px; color: #555; margin: 4px 0 8px; }
  .gol { padding: 24px; text-align: center; color: #666; font-size: 13px; }
  .verif { margin-top: 14px; border: 2px solid #8a8f9a; border-radius: 4px; padding: 7px 10px; page-break-inside: avoid; }
  .verif.ok { border-color: #27ae60; background: #f1fbf4; }
  .verif.eroare { border-color: #c0392b; background: #fdf1ef; }
  .verif table { width: auto; margin: 4px 0; }
  .verif td { border: none; padding: 1px 14px 1px 0; }
  .verif .status { font-weight: 700; }
  .verif.ok .status { color: #1e8449; }
  .verif.eroare .status { color: #c0392b; }
  @media print {
    body { background: #fff; }
    .bara { display: none; }
    .pagina { width: auto; margin: 0; padding: 0; box-shadow: none; }
  }
`;

function htmlVerificare(v) {
  const rand = (eticheta, valoare) =>
    `<tr><td>${eticheta}</td><td class="n"><strong>${lei(valoare)} lei</strong></td></tr>`;
  const status = v.ok
    ? 'CORECT - cele 3 totaluri generale sunt egale'
    : 'DIFERENTE intre totaluri - datele trebuie verificate';
  return `<div class="verif ${v.ok ? 'ok' : 'eroare'}">
    <strong>Verificare totaluri generale (valoare consum)</strong>
    <table>
      ${rand('Total consum pe facturi de intrare', v.totalFactura)}
      ${rand('Total consum pe produse finite', v.totalProduse)}
      ${rand('Total centralizator pe materiale', v.totalCentralizat)}
    </table>
    <span class="status">${status}</span>
  </div>`;
}

const GOL = '<p class="gol">Nu exista consumuri de productie in perioada selectata.</p>';

function htmlFactura(d) {
  const materiale = d.factura.materiale;
  if (materiale.length === 0) return GOL;

  // 12 coloane: factura (3) + Intrat (cant, pret, valoare) + Consumat anterior (2)
  // + Consumat in perioada (2) + Sold la sfarsitul perioadei (2)
  let corp = '';
  for (const m of materiale) {
    corp += `<tbody class="mat"><tr class="grup"><td colspan="12">${esc(denumireMaterial(m))} <span class="um">(${esc(m.um || '')})</span></td></tr>`;
    for (const r of m.randuri) {
      const fara = r.faraFactura;
      corp += `<tr${fara ? ' class="fara"' : ''}>
        <td>${esc(r.document)}</td>
        <td>${esc(r.furnizor || '')}</td>
        <td>${fara ? '' : dataRo(r.data)}</td>
        <td class="n">${fara ? '' : cant(r.intratCant)}</td>
        <td class="n">${fara ? '' : pret(r.intratPret)}</td>
        <td class="n">${fara ? '' : lei(r.intratVal)}</td>
        <td class="n">${zeroLiniuta(r.antCant, cant)}</td>
        <td class="n">${zeroLiniuta(r.antVal, lei)}</td>
        <td class="n">${cant(r.perCant)}</td>
        <td class="n">${lei(r.perVal)}</td>
        <td class="n">${fara ? '' : cant(r.soldCant)}</td>
        <td class="n">${fara ? '' : lei(r.soldVal)}</td>
      </tr>`;
    }
    const t = m.total;
    corp += `<tr class="sub">
      <td colspan="3">Total ${esc(m.denumire)}</td>
      <td class="n">${cant(t.intratCant)}</td><td></td><td class="n">${lei(t.intratVal)}</td>
      <td class="n">${zeroLiniuta(t.antCant, cant)}</td><td class="n">${zeroLiniuta(t.antVal, lei)}</td>
      <td class="n">${cant(t.perCant)}</td><td class="n">${lei(t.perVal)}</td>
      <td class="n">${cant(t.soldCant)}</td><td class="n">${lei(t.soldVal)}</td>
    </tr></tbody>`;
  }
  const g = d.factura.total;
  corp += `<tbody class="gen"><tr class="tot">
    <td colspan="3">TOTAL GENERAL (lei)</td>
    <td></td><td></td><td class="n">${lei(g.intratVal)}</td>
    <td></td><td class="n">${lei(g.antVal)}</td>
    <td></td><td class="n">${lei(g.perVal)}</td>
    <td></td><td class="n">${lei(g.soldVal)}</td>
  </tr></tbody>`;

  return `<table>
    <colgroup>
      <col style="width:14%"><col style="width:13%"><col style="width:7%">
      <col style="width:7%"><col style="width:6%"><col style="width:8.5%">
      <col style="width:6.5%"><col style="width:8.5%">
      <col style="width:7%"><col style="width:8.5%">
      <col style="width:6%"><col style="width:8%">
    </colgroup>
    <thead>
      <tr>
        <th rowspan="2">Factura / document</th><th rowspan="2">Furnizor</th><th rowspan="2">Data</th>
        <th colspan="3">Intrat</th><th colspan="2">Consumat anterior</th>
        <th colspan="2">Consumat in perioada</th><th colspan="2">Sold la sfarsitul perioadei</th>
      </tr>
      <tr>
        <th>Cantitate</th><th>Pret</th><th>Valoare</th>
        <th>Cantitate</th><th>Valoare</th>
        <th>Cantitate</th><th>Valoare</th>
        <th>Cantitate</th><th>Valoare</th>
      </tr>
    </thead>
    ${corp}
  </table>
  <p class="nota">Consum = iesiri de material pentru productie. Consumul se repartizeaza pe facturi in ordinea intrarii
  (cea mai veche factura se consuma prima) si se evalueaza la pretul de intrare al facturii; soldul la sfarsitul
  perioadei = cantitatea ramasa din factura x pretul de intrare. "Consumat anterior" = consumat inainte de inceputul
  perioadei. Consumul care depaseste intrarile inregistrate apare pe randul "Fara factura de intrare", la pretul
  inregistrat pe miscarea de consum. Se afiseaza doar facturile din care s-a consumat in perioada.</p>`;
}

const ANTET_T2 = `<thead><tr><th class="nr">Nr. crt.</th><th>Reper</th><th>UM</th>
  <th>Cantitate</th><th>Pret (lei)</th><th>Valoare totala (lei)</th></tr></thead>`;

function randuriMateriale(lista) {
  return lista.map((l, i) => `<tr>
    <td class="nr">${l.nr ?? i + 1}</td>
    <td>${esc(denumireMaterial(l))}</td>
    <td style="text-align:center">${esc(l.um || '')}</td>
    <td class="n">${cant(l.cantitate)}</td>
    <td class="n">${pret(l.pret)}</td>
    <td class="n">${lei(l.valoare)}</td>
  </tr>`).join('');
}

function htmlProductie(d) {
  const p = d.productie;
  if (p.produse.length === 0) return GOL;

  let h = '';
  for (const prod of p.produse) {
    const faraProdus = prod.produsFinitId === 0;
    const titlu = prod.cod ? `${prod.cod} - ${prod.denumire}` : prod.denumire;
    h += `<div class="bloc"><h3>${faraProdus ? '' : 'Produs finit: '}${esc(titlu)}</h3>
      <table class="t2">${ANTET_T2}<tbody>
        ${randuriMateriale(prod.materiale)}
        <tr class="sub"><td colspan="5">${faraProdus ? 'Total' : 'Total consum ' + esc(prod.denumire)}</td><td class="n">${lei(prod.totalValoare)}</td></tr>
      </tbody></table></div>`;
  }
  h += `<table class="general"><tbody>
    <tr class="tot"><td>TOTAL GENERAL CONSUM (pe produse finite)</td><td class="n">${lei(p.totalProduse)} lei</td></tr>
  </tbody></table>`;

  h += `<h3>Centralizator consum pe materiale</h3>
    <table class="t2">${ANTET_T2}<tbody>
      ${randuriMateriale(p.centralizat)}
      <tr class="tot"><td colspan="5">TOTAL</td><td class="n">${lei(p.totalCentralizat)}</td></tr>
    </tbody></table>`;
  return h;
}

// date = raspunsul API; forma = FORMA_FACTURA | FORMA_PRODUCTIE
export function construiesteHtmlRaport(date, forma) {
  const titluForma = forma === FORMA_PRODUCTIE
    ? 'Consum pe productie (pe fiecare produs finit)'
    : 'Consum pe facturi de intrare (intrat / consumat / sold)';
  const perioada = `${dataRo(date.dataInceput)} - ${dataRo(date.dataSfarsit)}`;
  const titlu = `Raport consum materiale ${perioada}`;
  const continut = forma === FORMA_PRODUCTIE ? htmlProductie(date) : htmlFactura(date);
  const generat = new Date().toLocaleString('ro-RO', { dateStyle: 'short', timeStyle: 'short' });

  return `<!DOCTYPE html><html lang="ro"><head><meta charset="utf-8"><title>${esc(titlu)}</title>
    <style>${STIL}</style></head><body>
    <div class="bara">
      <span>Previzualizare raport &middot; A4 landscape</span>
      <span class="spatiu"></span>
      <button type="button" class="print" onclick="window.print()">Print</button>
      <button type="button" class="inchide" onclick="window.close()">Inchide</button>
    </div>
    <div class="pagina">
      <h1>Raport consum materiale pe perioada</h1>
      <p class="meta">Perioada: <strong>${perioada}</strong> &middot; ${esc(titluForma)} &middot; Generat: ${esc(generat)}</p>
      ${continut}
      ${htmlVerificare(date.verificare)}
    </div></body></html>`;
}
